import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { Translate } from '~/shared/internationalization/translation';
import type { RealtimeEventListenerBuilder } from '~/shared/realtime-event-listener/realtime-event-listener';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type { RecommendedAgentsFetchers } from './agents/recommended-agents-fetchers';
import { createRecommendedAgentsFetchers } from './agents/recommended-agents-fetchers';
import type { ChatFetchers } from './chat/chat-fetchers';
import { createChatFetchers } from './chat/chat-fetchers';
import type { ChatAttachmentsFetchers } from './chat-attachments/chat-attachments-fetchers';
import { createChatAttachmentsFetchers } from './chat-attachments/chat-attachments-fetchers';
import type { CompanyProfileFetchers } from './company-profile/company-profile-fetchers';
import { createCompanyProfileFetchers } from './company-profile/company-profile-fetchers';
import type { CompanySettingsFetchers } from './company-settings/company-settings-fetchers';
import { createCompanySettingsFetchers } from './company-settings/company-settings-fetchers';
import type { DocumentsFetchers } from './documents/documents-fetchers';
import { createDocumentsFetchers } from './documents/documents-fetchers';
import type { GraphCanvasFetchers } from './graph-canvas/graph-canvas-fetchers';
import { createGraphCanvasFetchers } from './graph-canvas/graph-canvas-fetchers';
import type { InternalTrackingFetchers } from './internal-tracking/internal-tracking-fetchers';
import { createInternalTrackingFetchers } from './internal-tracking/internal-tracking-fetchers';
import type { AutomationFetchers } from './job-architecture/automation-fetchers';
import { createAutomationFetchers } from './job-architecture/automation-fetchers';
import type { CommentsFetchers } from './job-architecture/job-architecture-comments-fetchers';
import {
    createJobProfileCommentsFetchers,
    createProfileTaskCommentsFetchers,
} from './job-architecture/job-architecture-comments-fetchers';
import type { JobArchitectureFetchers } from './job-architecture/job-architecture-fetchers';
import { createJobArchitectureFetchers } from './job-architecture/job-architecture-fetchers';
import type { ProfileTaskFetchers } from './job-architecture/profile-task-fetchers';
import { createProfileTaskFetchers } from './job-architecture/profile-task-fetchers';
import type { NotificationsFetchers } from './notifications/notifications-fetchers';
import { createNotificationsFetchers } from './notifications/notifications-fetchers';
import type { PersonalHomeFetchers } from './personal-home/personal-home-fetchers';
import { createPersonalHomeFetchers } from './personal-home/personal-home-fetchers';
import type { PositionAlignmentFetchers } from './position-alignment/position-alignment-fetchers';
import { createPositionAlignmentFetchers } from './position-alignment/position-alignment-fetchers';
import type { AiTransformationFetchers } from './position-management/ai-transformation-fetchers';
import { createAiTransformationFetchers } from './position-management/ai-transformation-fetchers';
import type { PositionManagementFetchers } from './position-management/position-management-fetchers';
import { createPositionManagementFetchers } from './position-management/position-management-fetchers';
import type { PositionSetupFetchers } from './position-setup/position-setup-fetchers';
import { createPositionSetupFetchers } from './position-setup/position-setup-fetchers';
import { createSkillFetchers, type SkillFetchers } from './skill/skill-fetchers';
import type { UserManagementFetchers } from './user-management/user-management-fetchers';
import { createUserManagementFetchers } from './user-management/user-management-fetchers';
import type { WatchesFetchers } from './watches/watches-fetchers';
import { createWatchesFetchers } from './watches/watches-fetchers';

export type ApplicationFetchers = AiTransformationFetchers &
    AutomationFetchers &
    RecommendedAgentsFetchers &
    ChatAttachmentsFetchers &
    ChatFetchers &
    CompanyProfileFetchers &
    CompanySettingsFetchers &
    DocumentsFetchers &
    GraphCanvasFetchers &
    InternalTrackingFetchers &
    JobArchitectureFetchers &
    NotificationsFetchers &
    PersonalHomeFetchers &
    PositionAlignmentFetchers &
    PositionManagementFetchers &
    PositionSetupFetchers &
    ProfileTaskFetchers &
    SkillFetchers &
    UserManagementFetchers &
    WatchesFetchers & {
        readonly jobProfileComments: CommentsFetchers;
        readonly profileTaskComments: CommentsFetchers;
    };

export type { AssignableReviewersItem, AssignReviewerItem } from './job-architecture/job-architecture-fetchers';

export type ApplicationFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeApiFetcher: HttpFetcherWithBaseUrl;
    readonly realtimeBaseUrl: URL;
    readonly realtimeEventListenerBuilder: RealtimeEventListenerBuilder;
    readonly translate: Translate;
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
    readonly validatingRealtimeApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createApplicationFetchers = (dependencies: ApplicationFetchersDependencies): ApplicationFetchers => {
    return {
        ...createAiTransformationFetchers(dependencies),
        ...createAutomationFetchers(dependencies),
        ...createRecommendedAgentsFetchers(dependencies),
        ...createChatAttachmentsFetchers(dependencies),
        ...createChatFetchers(dependencies),
        ...createCompanyProfileFetchers(dependencies),
        ...createCompanySettingsFetchers(dependencies),
        ...createDocumentsFetchers(dependencies),
        ...createGraphCanvasFetchers(dependencies),
        ...createJobArchitectureFetchers(dependencies),
        ...createNotificationsFetchers(dependencies),
        ...createPersonalHomeFetchers(dependencies),
        ...createPositionAlignmentFetchers(dependencies),
        ...createPositionManagementFetchers(dependencies),
        ...createPositionSetupFetchers(dependencies),
        ...createProfileTaskFetchers(dependencies),
        ...createSkillFetchers(dependencies),
        ...createInternalTrackingFetchers(dependencies),
        ...createUserManagementFetchers(dependencies),
        ...createWatchesFetchers(dependencies),
        jobProfileComments: createJobProfileCommentsFetchers(dependencies),
        profileTaskComments: createProfileTaskCommentsFetchers(dependencies),
    };
};
